import { Component, ElementRef, Injector, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  ContratoService, ConvenioService, DespesaService, EmpenhoAnexoService, EmpenhoService, FichaDespesaService, LicitacaoService
} from 'administrativo-lib';
import { CompraService, ModalidadeService } from 'compra-lib';
import {
  BaseResourceFormComponent,
  Compra, Contrato, Convenio, DateFormatPipe, Despesa, EddyAutoComplete, Empenho,
  EmpenhoAnexo, Exercicio, Favorecido, FavorecidoService, FichaDespesa,
  FuncaoService, GlobalService, Licitacao, LoginContabil, Modalidade, OrgaoAssinaturaService
} from 'eddydata-lib';
import { MessageService } from 'primeng/api';
import { AutoComplete } from 'primeng/autocomplete';
import { Calendar } from 'primeng/calendar';
import { takeUntil } from 'rxjs/operators';
import * as toastr from 'toastr';
import { NotaEmpenho } from '../../relatorio/contabil/nota-empenho';

declare var $: any;

@Component({
  selector: 'lib-empenho-form',
  templateUrl: './empenho-form.component.html'
})
export class EmpenhoFormComponent extends BaseResourceFormComponent<Empenho, LoginContabil> implements OnInit {

  // ========================================================================
  // ----------------------- DECLARAÇÃO DE VARIAVEIS ------------------------
  // ========================================================================
  @ViewChild('compra_') inputField: AutoComplete;
  public favorecidoAutoComplete: EddyAutoComplete<Favorecido>;
  public fichaAutoComplete: EddyAutoComplete<FichaDespesa>;
  public subElementoAutoComplete: EddyAutoComplete<Despesa>;
  public compraAutoComplete: EddyAutoComplete<Compra>;
  public licitacaoAutoComplete: EddyAutoComplete<Licitacao>;
  public contratoAutoComplete: EddyAutoComplete<Contrato>;
  public convenioAutoComplete: EddyAutoComplete<Convenio>;
  public listaModalidades: Array<Modalidade>;
  public listaTipos: Array<any>;
  public listaCompras: Array<any>;
  public listaLicitacoes: Array<any>;
  public listaContratos: Array<any>;
  public listaConvenios: Array<any>;
  public dotacao: number;
  public reservado: number;
  public anterior: number;
  public saldo: number;
  public saldoContrato: number = 0;
  public saldoConvenio: number = 0;
  public empenhado: number;
  public liquidado: number;
  public pago: number;
  private fichaOriginal: FichaDespesa;

  public listaAnexos = new Array<EmpenhoAnexo>();
  public uploadedFiles: any[] = [];

  // ========================================================================
  // ------------------------------ CONSTRUTOR ------------------------------
  // ========================================================================
  constructor(
    protected injector: Injector,
    private messageService: MessageService,
    protected globalService: GlobalService,
    protected funcaoService: FuncaoService,
    protected favorecidoService: FavorecidoService,
    protected anexoService: EmpenhoAnexoService,
    protected despesaService: DespesaService,
    protected fichaService: FichaDespesaService,
    protected compraService: CompraService,
    protected licitacaoService: LicitacaoService,
    protected contratoService: ContratoService,
    protected convenioService: ConvenioService,
    protected assinaturaService: OrgaoAssinaturaService,
    protected modalidadeService: ModalidadeService,
    protected empenhoService: EmpenhoService) {
    super(new Empenho(), injector, Empenho.converteJson, empenhoService);
  }

  // ========================================================================
  // -------------------------- MÉTODOS ABSTRAÍDOS --------------------------
  // ========================================================================
  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      numero: [null, [Validators.required]],
      valor_empenho: null,
      data_empenho: [null, [Validators.required]],
      data_vencimento: [null],
      especie: ['EMO', [Validators.required]],
      tipo_empenho: ['O', [Validators.required]],
      fase4: [0, [Validators.required]],
      disponibilidade_anterior: [false, [Validators.required]],
      adiantamento: [0, [Validators.required]],
      processo: [null],
      documento: [null],
      modalidade: [null, [Validators.required]],
      historico: [null, [Validators.required, Validators.minLength(2)]],
      ficha: [null, [Validators.required]],
      orgao: [this.login.orgao, [Validators.required]],
      exercicio: [this.login.exercicio, [Validators.required]],
      favorecido: [null, [Validators.required]],
      subelemento: [null, [Validators.required]],
      compra: [null],
      licitacao: [null],
      ata: [null],
      contrato: [null],
      convenio: [null],
      mes: [null],
      aux: [0, [Validators.required]],
      usuario_cadastro: [this.login.usuario, [Validators.required]],
      inscrito_resto: [false]
    });
  }

  protected campoFoco(): ElementRef | AutoComplete | Calendar {
    this.inputField.focusInput()
    return null;
  }

  protected parametrosExtras(): {} {
    return {
      relations: 'ficha,modalidade,licitacao,subelemento,compra,convenio,favorecido,favorecido.tipo,contrato,'
        + 'ficha.despesa,ficha.acao,exercicio,orgao'
    };
  }

  protected async afterLoad() {
    if (!this.podeAlterar(this.entidade.data_empenho)) {
      this.router.navigate(['/empenhos']);
      toastr.warning('Não é possível alterar. Prazo esgotado!');
    }

    this.entidade.data_empenho = new DateFormatPipe().transform(this.entidade.data_empenho, []);
    this.entidade.data_vencimento = new DateFormatPipe().transform(this.entidade.data_vencimento, []);

    await this.loadTotalizadores();

    this.favorecidoAutoComplete.id = this.entidade.favorecido ? this.entidade.favorecido.id : null;
    this.fichaAutoComplete.id = this.entidade.ficha ? this.entidade.ficha.numero : null;
    this.subElementoAutoComplete.id = this.entidade.subelemento ? this.entidade.subelemento.codigo : null;
    this.compraAutoComplete.id = this.entidade.compra ? this.entidade.compra.id : null;
    this.licitacaoAutoComplete.id = this.entidade.licitacao ? this.entidade.licitacao.id : null;
    this.contratoAutoComplete.id = this.entidade.contrato ? this.entidade.contrato.id : null;
    this.convenioAutoComplete.id = this.entidade.convenio ? this.entidade.convenio.id : null;

    if (this.entidade.especie === 'EOA') {
      this.entidadeForm.get('valor_empenho').setValue(this.entidade.valor_empenho * -1);
    }

    this.anexoService.filtrar(1, -1, { empenho_id: this.entidade.id })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaAnexos = res.content;
      });

    this.fichaOriginal = this.entidade.ficha;
    if (this.entidade.contrato) {
      this.obterSaldoContrato(this.entidade.contrato);
    }
    if (this.entidade.convenio) {
      this.obterSaldoConvenio();
    }
  }

  protected afterInit(): void {
    this.modalidadeService.filtrar(1, -1, { 'cidade.id': this.login.cidade.id, orderBy: 'codigo' }).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        this.listaModalidades = res ? res.content : new Array<Modalidade>();
      }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));
    this.listaTipos = this.globalService.obterListaTiposEmpenhos();
    this.carregarAutoCompletes();
    if (this.currentActionRoute === 'novo') {
      this.empenhoService.proximoNumeroEmpenho(this.login.exercicio.id, this.login.orgao.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((res) => {
          this.entidadeForm.get('numero').setValue(res);
          this.empenhoService.ultimaDataEmpenhada(this.login.exercicio.id, this.login.orgao.id)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((dataEmp) => {
              if (dataEmp === 0) {
                this.entidade.data_empenho = new Date();
              } else {
                this.entidade.data_empenho = new DateFormatPipe().transform(dataEmp, []);
              }
              this.entidadeForm.get('data_empenho').setValue(this.entidade.data_empenho);
              this.entidadeForm.get('mes').setValue(this.entidade.data_empenho.getUTCMonth() + 1);
            });
        });
    }
  }

  protected beforeSubmit() {
    try {
      if (!this.entidadeForm.get('numero').value) {
        throw new Error('Informe o número do empenho');
      }
      if (!this.entidadeForm.get('exercicio').value) {
        throw new Error('Informe o exercício do empenho');
      }
      if (!this.entidadeForm.get('orgao').value) {
        throw new Error('Informe o órgão do empenho');
      }
      if (!this.entidadeForm.get('subelemento').value) {
        throw new Error('Informe a despesa do empenho');
      }
      if (!this.entidadeForm.get('favorecido').value) {
        throw new Error('Informe o favorecido do empenho');
      }
      if (!this.entidadeForm.get('ficha').value) {
        throw new Error('Informe a ficha do empenho');
      }
      if (!this.entidadeForm.get('historico').value) {
        throw new Error('Informe o histórico do empenho');
      }
      if (!this.entidadeForm.get('valor_empenho').value) {
        throw new Error('Informe o valor do empenho');
      }
      if (this.entidadeForm.get('contrato').value && this.saldoContrato <= 0) {
        throw new Error('Não há saldo no contrato para empenhar, veja se existe aditivos!');
      }
      if (this.entidadeForm.get('convenio').value && this.saldoConvenio <= 0) {
        throw new Error('Não há saldo no convênio para empenhar!');
      }
      if (this.entidadeForm.get('valor_empenho').value <= 0) {
        this.entidadeForm.get('valor_empenho').setValue(this.entidadeForm.get('valor_empenho').value * -1);
      }

      if (this.entidadeForm.get('licitacao').value === '') {
        this.entidadeForm.get('licitacao').setValue(null);
      }
      if (this.entidadeForm.get('convenio').value === '') {
        this.entidadeForm.get('convenio').setValue(null);
      }
      const dtEmpenho: Date = this.entidadeForm.get('data_empenho').value;
      if (dtEmpenho.getFullYear() !== this.login.exercicio.ano) {
        throw new Error('O ano da data do empenho está diferente do exercício logado');
      }
      this.entidadeForm.get('mes').setValue(dtEmpenho.getUTCMonth() + 1);
      console.log(this.entidadeForm);
      
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: e });
      throw e;
    }
  }

  protected afterSubmit(ent: Empenho) {
    this.mensagemSucesso = `Registro salvo com sucesso!\nEmpenho nº ${ent.numero}`;
    if (this.currentActionRoute === 'novo' && this.login.redirecionar_liquidar_pagar
      && this.podeIncluir('/liquidacoes')) {
      this.router.navigate(['/liquidacoes', 'novo', ent.id]);
    } else if (this.currentActionRoute === 'novo' && !this.limparTela) {
      this.router.navigate(['/empenhos', ent.id, 'editar']);
    }
  }

  // ========================================================================
  // -------------------------- MÉTODOS DA CLASSE ---------------------------
  // ========================================================================

  private async loadTotalizadores() {
    this.empenhado = 0;
    this.liquidado = 0;
    this.pago = 0;
    this.dotacao = 0;
    this.anterior = 0;
    this.reservado = 0;

    const exercicio: Exercicio = this.entidadeForm.get('exercicio').value
      ? this.entidadeForm.get('exercicio').value
      : this.login.exercicio;

    let totalizadores: any;
    if (this.entidade.id) {
      totalizadores = await this.empenhoService.totalizadores(exercicio.id, this.login.orgao.id, this.entidade.numero).toPromise();
    } else {
      const ficha: FichaDespesa = this.entidadeForm.get('ficha').value
        ? this.entidadeForm.get('ficha').value
        : this.entidade.ficha;
      const dataEmpenho: Date = this.entidadeForm.get('data_empenho').value
        ? this.entidadeForm.get('data_empenho').value
        : this.entidade.data_empenho;
      if (ficha && dataEmpenho) {
        totalizadores = await this.empenhoService.totalizadoresFicha(
          exercicio.id, this.login.orgao.id, ficha.id,
          String(this.funcaoService.converteDataSQL(new DateFormatPipe().transform(dataEmpenho, ['local'])))).toPromise();
        totalizadores = { content: totalizadores };
      }
    }

    if (totalizadores && totalizadores.content) {
      totalizadores = totalizadores.content[0] ? totalizadores.content[0] : totalizadores.content;
      this.empenhado = totalizadores.total_empenhado;
      this.liquidado = totalizadores.total_liquidado;
      this.pago = totalizadores.total_pago;

      this.dotacao = +totalizadores.total_dotacao_ficha + +totalizadores.total_creditado_ficha;

      const valorAnterior = totalizadores.total_empenhado_ficha_anterior ?
        +totalizadores.total_empenhado_ficha_anterior : +totalizadores.total_empenhado_ficha;

      this.anterior = +this.dotacao - +valorAnterior;
      this.reservado = totalizadores.total_reservado;
    }
  }

  private carregarAutoCompletes() {
    // autocomplete para favorecido
    this.favorecidoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('favorecido'), this.favorecidoService,
      'id', ['nome'], { cidade_id: this.login.cidade.id, relations: 'tipo', orderBy: 'nome' }, { number: ['id', 'cpf_cnpj'], text: ['nome'] },
      () => {
        this.preencherDadosUltimoEmpenho();
      }
    );

    // autocomplete para ficha
    this.fichaAutoComplete = new EddyAutoComplete(this.entidadeForm.get('ficha'), this.fichaService,
      'numero', ['ficha.despesa.codigo', 'ficha.despesa.nome'],
      { exercicio_id: this.login.exercicio.id, orgao_id: this.login.orgao.id, relations: 'despesa', orderBy: 'despesa.nome' },
      { number: ['numero'], text: ['despesa.codigo', 'despesa.nome'] },
      () => {
        const ficha: FichaDespesa = this.entidadeForm.get('ficha').value;
        if (ficha && (!this.fichaOriginal || this.fichaOriginal.id !== ficha.id)) {
          this.fichaOriginal = undefined;
          this.subElementoFiltrar(ficha.despesa.codigo.substring(0, 6));
        }
        // verifica se a ficha de despesa é uma ficha de adiantamento ou diárias
        if (ficha && (ficha.despesa.codigo.startsWith('339033') || ficha.despesa.codigo.startsWith('339014'))) {
          this.entidadeForm.get('adiantamento').setValue(true);
        } else {
          this.entidadeForm.get('adiantamento').setValue(false);
        }
        this.alterarSubElemento();
        this.loadTotalizadores();
        this.preencherDadosUltimoEmpenho();
        if (this.entidadeForm.get('contrato').value) {
          this.obterSaldoContrato(this.entidadeForm.get('contrato').value);
        }
      }
    );

    // autocomplete para subelemento
    this.subElementoFiltrar();

    // autocomplete para compra
    this.compraAutoComplete = new EddyAutoComplete(this.entidadeForm.get('compra'), this.compraService, 'numero', ['numero'],
      { exercicio_id: this.login.exercicio.id, orgao_id: this.login.orgao.id, relations: 'exercicio', orderBy: 'numero' },
      { number: ['id', 'numero'] }
    );

    // autocomplete para licitacao
    this.licitacaoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('licitacao'), this.licitacaoService,
      'numero', ['numero'], { orgao_id: this.login.orgao.id, orderBy: 'numero' }, { number: ['numero'] }
    );

    // autocomplete para contrato
    this.contratoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('contrato'), this.contratoService,
      'numero', ['numero', 'aditivo'], {
      orgao_id: this.login.orgao.id, orderBy: 'numero',
      relations: 'licitacao,tipo_contratacao'
    }, { text: ['numero'] },
      () => {
        const contrato: Contrato = this.entidadeForm.get('contrato').value
          ? this.entidadeForm.get('contrato').value
          : this.entidade.contrato;
        if (contrato) {
          this.entidadeForm.get('licitacao').setValue(null);
          this.entidadeForm.get('modalidade').setValue(null);
          this.entidadeForm.get('contrato').setValue(contrato);
          this.entidadeForm.get('processo').setValue(contrato.processo);
          if (contrato.licitacao) {
            this.licitacaoService.obter({ id: contrato.licitacao.id, relations: 'modalidade' }).pipe(takeUntil(this.unsubscribe))
              .subscribe((res) => {
                this.entidadeForm.get('licitacao').setValue(res);
                this.entidadeForm.get('modalidade').setValue(res.modalidade);
              }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error }));
          }
          this.obterSaldoContrato(contrato);
        }
      }
    );

    // autocomplete para convenio
    this.convenioAutoComplete = new EddyAutoComplete(this.entidadeForm.get('convenio'), this.convenioService,
      'numero', ['numero', 'ano'], { orgao_id: this.login.orgao.id, orderBy: 'numero' }, { number: ['id', 'numero'] }
    );
  }

  private subElementoFiltrar(codigo?: string) {
    this.subElementoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('subelemento'), this.despesaService, 'codigo', ['nome'],
      Object.assign({}, { exercicio_id: this.login.exercicio.id, nivel: 6, orderBy: 'codigo', }, { ['codigo$like']: codigo + '%' }),
      { number: ['id', 'codigo'], text: ['nome'] },
      () => {
        this.preencherHistoricoSubelemento();
      }
    );
  }

  private alterarSubElemento() {
    const ficha: FichaDespesa = this.entidadeForm.get('ficha').value;
    if (ficha) {
      const fichaSubElemento: string = ficha.despesa.codigo.substring(0, 6);
      const subElemento: Despesa = this.entidadeForm.get('subelemento').value;
      if (!subElemento || fichaSubElemento !== subElemento.codigo.substring(0, 6)) {
        this.subElementoAutoComplete.id = fichaSubElemento;
        this.entidadeForm.get('subelemento').setValue(null);
      } else {
        this.subElementoAutoComplete.id = subElemento.codigo;
      }
    }
  }

  private preencherDadosUltimoEmpenho() {
    if (this.login.repetir_dados_empenho && !this.entidade.id) {
      const favorecido: Favorecido = this.entidadeForm.get('favorecido').value;
      const ficha: FichaDespesa = this.entidadeForm.get('ficha').value;

      if (favorecido && ficha) {
        this.empenhoService
          .obter({
            orgao_id: this.login.orgao.id,
            exercicio_id: this.login.exercicio.id,
            favorecido_id: favorecido.id,
            ficha_id: ficha.id,
            orderBy: 'id$DESC',
            relations: 'subelemento,licitacao,convenio,contrato,modalidade'
          })
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(ent => {
            if (ent) {
              if (ent.subelemento) {
                this.entidadeForm.get('subelemento').setValue(ent.subelemento);
                this.subElementoAutoComplete.id = ent.subelemento.codigo;
              }
              if (ent.licitacao) {
                this.entidadeForm.get('licitacao').setValue(ent.licitacao);
              }
              if (ent.modalidade) {
                this.entidadeForm.get('modalidade').setValue(ent.modalidade);
              }
              if (ent.processo) {
                this.entidadeForm.get('processo').setValue(ent.processo);
              }
              if (ent.convenio) {
                this.entidadeForm.get('convenio').setValue(ent.convenio);
              }
              if (ent.contrato) {
                this.entidadeForm.get('contrato').setValue(ent.contrato);
              }
              if (ent.historico) {
                this.entidadeForm.get('historico').setValue(ent.historico);
              }
              if (ent.adiantamento) {
                this.entidadeForm.get('adiantamento').setValue(ent.adiantamento);
              }
            }
          });
      }
    }
  }

  private preencherHistoricoSubelemento() {
    if (!this.entidade.id && !this.login.repetir_dados_empenho && !this.entidadeForm.get('historico').value) {
      const subelemento: Despesa = this.entidadeForm.get('subelemento').value;
      if (subelemento) {
        this.entidadeForm.get('historico').setValue(subelemento.nome);
      }
    }
  }

  imprimir() {
    const parametros = {};

    let relations = 'ficha.executora.unidade,ficha.despesa,ficha.funcao,ficha.subfuncao,ficha.programa,ficha.acao,ficha.recurso,ficha.aplicacao,';
    relations += 'licitacao,modalidade,subelemento,favorecido,favorecido.tipo,contrato,convenio';
    parametros['relations'] = relations;
    parametros['exercicio_id'] = this.login.exercicio.id;
    parametros['orgao_id'] = this.login.orgao.id;
    parametros['id'] = this.entidade.id;
    this.empenhoService
      .extendido(1, -1, parametros)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(lista => {
        new NotaEmpenho(this.assinaturaService).imprimir(lista.content, this.login);
      },
        (error) => this.messageService.add(
          { severity: 'error', summary: 'Atenção!', detail: error.error && error.error.payload ? error.error.payload : error }
        )
      );
  }

  public async onUpload(event: any, fileUpload: any) {
    if (!event.files || event.files.length === 0) {
      this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione o(s) arquivo(s)' });
      return;
    }
    if (this.entidadeForm.valid) {
      this.beforeSubmit();
      const entidade: Empenho = this.jsonDataToResourceFn(this.entidadeForm.value);
      try {
        for (const file of event.files) {
          this.uploadedFiles.push(file);
        }
        this.anexoService
          .upload(entidade, this.uploadedFiles).pipe(takeUntil(this.unsubscribe))
          .subscribe((res) => {
            $('#dialogUpload').modal('hide');
            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
              this.router.navigate(['/empenhos', res.id, 'editar']));
            this.messageService.add({ severity: 'success', summary: 'Informação', detail: 'Documento enviado com sucesso!' });
          }, (res) => {
            this.messageService.add({ severity: 'error', summary: 'Validação', detail: res.error.payload });
            this.uploadedFiles.length = 0;
          });
      } catch (ex) {
        toastr.error(ex.message ? ex.message : ex);
      }
      fileUpload.clear();
    }
  }

  public download(arquivo: any) {
    this.anexoService
      .download(arquivo.documento).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) => {
        const blob = new Blob([res], { type: arquivo.tipo });
        const downloadURL = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadURL;
        link.download = arquivo.nome;
        link.click();
        window.URL.revokeObjectURL(downloadURL);
      });
  }

  public obterSaldoConvenio() {
    if (this.entidadeForm.get('convenio').value !== null) {
      this.empenhoService.totalEmpenhadoPorContrato(this.entidadeForm.get('convenio').value.id, this.login.orgao.id)
        .subscribe((saldo) => {
          this.saldoConvenio = this.entidadeForm.get('convenio').value.valor_convenio - saldo;
        });
    }
  }


  public obterSaldoContrato(contrato: Contrato) {
    this.empenhoService.totalEmpenhadoPorContrato(this.entidadeForm.get('contrato').value.id, this.login.orgao.id)
      .subscribe((saldo) => {
        if (contrato.tipo_contratacao && contrato.tipo_contratacao.material && contrato.tipo_contratacao.servico) {
          if (this.entidadeForm.get('ficha').value && this.entidadeForm.get('ficha').value.despesa.codigo.substr(0, 6) == '449052') {
            this.saldoContrato = this.entidadeForm.get('contrato').value.valor_material - saldo;
          } else if (this.entidadeForm.get('ficha').value && (
            this.entidadeForm.get('ficha').value.despesa.codigo.substr(0, 6) == '339036' || this.entidadeForm.get('ficha').value.despesa.codigo.substr(0, 6) == '339039')) {
            this.saldoContrato = this.entidadeForm.get('contrato').value.valor_servico - saldo;
          } else {
            this.saldoContrato = this.entidadeForm.get('contrato').value.valor_contrato - saldo;
          }
        } else {
          this.saldoContrato = this.entidadeForm.get('contrato').value.valor_contrato - saldo;
        }
      });
  }

  public voltar() {
    switch (this.login.sistema) {
      case 'contabil':
      case 'controle-interno':
        this.router.navigate(['/contabilidade']);
        break;
      default:
        this.sair();
        break;
    }
  }
}
